import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShieldCheck, Send, ArrowLeft, MessageCircle, MoreVertical, Ban, Flag, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChatView({ token, matchId, matchUser, currentUser, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [actionPending, setActionPending] = useState("");
  const [banner, setBanner] = useState("");
  const [reveal, setReveal] = useState({
    you_revealed: false,
    they_revealed: false,
    both_revealed: false,
    photo: null,
    name: null,
  });
  const [revealSubmitting, setRevealSubmitting] = useState(false);
  const bottomRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (matchId) {
      fetchMessages();
      fetchRevealState();
      intervalRef.current = setInterval(() => {
        fetchMessages();
        fetchRevealState();
      }, 3000);
      return () => clearInterval(intervalRef.current);
    }
  }, [matchId]);

  const fetchRevealState = async () => {
    try {
      const res = await fetch(`${API}/matches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const m = (data.matches || []).find((x) => x.match_id === matchId);
      if (m) {
        setReveal({
          you_revealed: !!m.you_revealed,
          they_revealed: !!m.they_revealed,
          both_revealed: !!m.both_revealed,
          photo: m.user?.photos?.[0] || null,
          name: m.user?.name || null,
        });
      }
    } catch (err) {
      // silent
    }
  };

  const handleRevealPhoto = async () => {
    if (revealSubmitting || reveal.you_revealed) return;
    setRevealSubmitting(true);
    try {
      const res = await fetch(`${API}/matches/${matchId}/reveal-photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setReveal((r) => ({
          ...r,
          you_revealed: true,
          both_revealed: !!data.both_revealed,
        }));
        await fetchRevealState();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRevealSubmitting(false);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API}/messages/${matchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (err) {
      console.error(err);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      await fetch(`${API}/messages/${matchId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newMsg.trim() }),
      });
      setNewMsg("");
      fetchMessages();
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleBlock = async () => {
    if (!matchUser?.id) return;
    if (!window.confirm(`Block ${matchUser.name}? They won't be able to message you again.`)) return;
    setActionPending("block");
    try {
      await fetch(`${API}/users/block`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target_user_id: matchUser.id }),
      });
      setBanner(`${matchUser.name} has been blocked.`);
      setShowMenu(false);
      setTimeout(() => onBack && onBack(), 1500);
    } catch (err) {
      console.error(err);
    } finally {
      setActionPending("");
    }
  };

  const handleReport = async () => {
    if (!matchUser?.id) return;
    const reason = window.prompt(`Report ${matchUser.name}. Please describe what happened:`, "");
    if (reason === null) return;
    setActionPending("report");
    try {
      await fetch(`${API}/users/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target_user_id: matchUser.id, reason: reason.trim() }),
      });
      setBanner("Report submitted. Admin will review it.");
      setShowMenu(false);
      setTimeout(() => onBack && onBack(), 1800);
    } catch (err) {
      console.error(err);
    } finally {
      setActionPending("");
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#050505]" data-testid="chat-view">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-[#1F2027] bg-[#0C0D10]">
        <button
          data-testid="chat-back-btn"
          onClick={onBack}
          className="text-[#8A8A93] hover:text-[#FAFAFA] transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div
          className="w-10 h-10 rounded-full overflow-hidden border border-[#1F2027] relative"
          data-testid="chat-header-avatar"
        >
          {reveal.both_revealed && reveal.photo ? (
            <img
              src={reveal.photo}
              alt=""
              className="w-full h-full object-cover"
              data-testid="chat-header-photo-revealed"
            />
          ) : (
            <div
              className="w-full h-full bg-gradient-to-br from-[#2A0F1D] via-[#1A0F2E] to-[#0A0A0C] flex items-center justify-center"
              data-testid="chat-header-photo-blurred"
            >
              <EyeOff size={14} className="text-white/40" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-[#FAFAFA] font-medium text-sm">
              {matchUser?.name}
            </h3>
            <ShieldCheck className="w-3.5 h-3.5 text-[#00FF66]" />
          </div>
          <span
            className="text-[#8A8A93] text-xs"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            FBI VERIFIED
          </span>
        </div>

        {/* Block / Report dropdown */}
        <div className="relative">
          <button
            type="button"
            data-testid="chat-menu-btn"
            onClick={() => setShowMenu((v) => !v)}
            className="text-[#8A8A93] hover:text-[#FAFAFA] transition-colors p-1.5 rounded hover:bg-white/5"
            aria-label="More options"
          >
            <MoreVertical size={18} />
          </button>
          {showMenu && (
            <div
              data-testid="chat-menu-dropdown"
              className="absolute right-0 top-9 z-50 w-44 bg-[#0C0D10] border border-[#1F2027] shadow-2xl py-1"
            >
              <button
                data-testid="chat-block-btn"
                onClick={handleBlock}
                disabled={actionPending === "block"}
                className="w-full text-left px-4 py-2.5 text-sm text-[#FAFAFA] hover:bg-[#1F2027] flex items-center gap-2 disabled:opacity-50"
              >
                <Ban size={14} className="text-[#FF6B6B]" />
                {actionPending === "block" ? "Blocking..." : "Block User"}
              </button>
              <button
                data-testid="chat-report-btn"
                onClick={handleReport}
                disabled={actionPending === "report"}
                className="w-full text-left px-4 py-2.5 text-sm text-[#FAFAFA] hover:bg-[#1F2027] flex items-center gap-2 disabled:opacity-50"
              >
                <Flag size={14} className="text-[#FFB347]" />
                {actionPending === "report" ? "Reporting..." : "Report User"}
              </button>
            </div>
          )}
        </div>
      </div>

      {banner && (
        <div className="bg-[#1A4D2E] text-[#9EE5B5] text-xs px-6 py-2 text-center border-b border-[#0F3E1F]">
          {banner}
        </div>
      )}

      {/* Photo reveal banner — mutual consent to unblur photos */}
      {!reveal.both_revealed && (
        <div
          data-testid="chat-reveal-banner"
          className="px-6 py-3 border-b border-[#1F2027] bg-gradient-to-r from-[#1A0F2E] via-[#0C0D10] to-[#2A0F1D] flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <EyeOff size={14} className="text-[#B084FF] shrink-0" />
            <span
              className="text-white/85 text-xs truncate"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              data-testid="chat-reveal-status"
            >
              {reveal.you_revealed && !reveal.they_revealed &&
                "You revealed. Waiting for them to reveal too…"}
              {!reveal.you_revealed && reveal.they_revealed &&
                "They revealed to you. Tap to reveal back."}
              {!reveal.you_revealed && !reveal.they_revealed &&
                "Photos are blurred. Reveal when you both feel ready."}
            </span>
          </div>
          <button
            data-testid="chat-reveal-btn"
            onClick={handleRevealPhoto}
            disabled={reveal.you_revealed || revealSubmitting}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 h-7 text-[11px] font-medium tracking-wide transition-all ${
              reveal.you_revealed
                ? "bg-white/[0.06] text-white/50 cursor-default"
                : "bg-[#FF3B6B] text-white hover:bg-[#E8335F]"
            }`}
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            <Eye size={12} />
            {reveal.you_revealed ? "You revealed" : revealSubmitting ? "Revealing…" : "Reveal photo"}
          </button>
        </div>
      )}

      {reveal.both_revealed && (
        <div
          data-testid="chat-reveal-done"
          className="px-6 py-2 bg-[#0F3E1F] border-b border-[#1A4D2E] text-[#9EE5B5] text-xs text-center flex items-center justify-center gap-2"
        >
          <Eye size={12} />
          You both revealed — photos are now unlocked
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
        {messages.length === 0 && (
          <div className="text-center py-20">
            <MessageCircle className="w-10 h-10 text-[#8A8A93] mx-auto mb-3" />
            <p className="text-[#8A8A93] text-sm">
              Say hello to {matchUser?.name}!
            </p>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`message-bubble ${
              msg.sender_id === currentUser?.id
                ? "message-sent"
                : "message-received"
            }`}
            data-testid={`message-${msg.id}`}
          >
            {msg.content}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={sendMessage}
        className="px-6 py-4 border-t border-[#1F2027] bg-[#0C0D10] flex gap-3"
      >
        <Input
          data-testid="chat-input"
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          placeholder="Type a message..."
          className="bg-[#050505] border-[#1F2027] text-[#FAFAFA] rounded-none focus:ring-1 focus:ring-[#00FF66] focus:border-[#00FF66] h-11 flex-1"
        />
        <Button
          data-testid="chat-send-btn"
          type="submit"
          disabled={sending || !newMsg.trim()}
          className="bg-[#00FF66] text-black rounded-none border border-[#00FF66] hover:bg-[#33FF85] h-11 px-5"
        >
          <Send size={16} />
        </Button>
      </form>
    </div>
  );
}
